import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import DeleteLedgerDialog from "../components/DeleteLedgerDialog";
import GoldSummaryPanel from "../components/GoldSummaryPanel";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { Search, FileText, ChevronLeft, Trash2, ArrowUpDown, Check, CalendarClock, Folder, FolderOpen } from "lucide-react";

const FILTERS = {
    ALL: "all",
    A_Z: "a_z",
    Z_A: "z_a",
    GOLD_HAS: "gold_has", // positive (red) — owed to us
    GOLD_OWES: "gold_owes", // negative (green) — we owe
    CASH_HAS: "cash_has",
    CASH_OWES: "cash_owes",
};

export default function History() {
    const { t, lang } = useLang();
    const { kind: urlKind } = useParams(); // "client" | "factory" | "daily" | "voucher_shop" | "voucher_customer"
    const kind = ["client", "daily", "voucher_shop", "voucher_customer"].includes(urlKind) ? urlKind : "factory";
    const isVoucher = kind === "voucher_shop" || kind === "voucher_customer";

    // Daily records get a hierarchical Year → Month → Day drill-down so the
    // list stays usable as records accumulate over the years.
    if (kind === "daily") {
        return <DailyHistoryTree t={t} lang={lang} />;
    }

    return <FlatHistory t={t} lang={lang} kind={kind} isVoucher={isVoucher} />;
}

function FlatHistory({ t, lang, kind, isVoucher }) {
    const [search, setSearch] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [filter, setFilter] = useState(FILTERS.ALL);

    const load = async (q, { silent = false } = {}) => {
        if (!silent) setLoading(true);
        try {
            const { data } = await api.get("/ledgers", {
                params: { kind, ...(q ? { search: q } : {}) },
            });
            setItems(Array.isArray(data) ? data : []);
        } catch {
            if (!silent) setItems([]);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        load("");
        setFilter(FILTERS.ALL);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kind]);

    useEffect(() => {
        const t = setTimeout(() => load(search), 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    // Silent auto-refresh every 10s while the tab is visible — new/updated
    // records from other users (or other devices) appear without manual reload.
    const silentRefresh = useCallback(() => {
        load(search, { silent: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, kind]);
    useAutoRefresh(silentRefresh, { intervalMs: 10000 });

    const pageTitle =
        kind === "client" ? t.clientRecordsTitle
        : kind === "daily" ? t.dailyRecordsTitle
        : kind === "voucher_customer" ? t.voucherCustomerTile
        : kind === "voucher_shop" ? t.voucherShopTile
        : t.factoryRecordsTitle;
    const pageSubtitle =
        kind === "client" ? t.clientRecordsSubtitle
        : kind === "daily" ? t.dailyRecordsSubtitle
        : kind === "voucher_customer" ? t.voucherCustomerTileDesc
        : kind === "voucher_shop" ? t.voucherShopTileDesc
        : t.factoryRecordsSubtitle;
    const showCash = kind === "factory";
    const supportsFilters = kind === "client" || kind === "factory";

    // Apply the active sort/filter on top of the server-side search results.
    const visibleItems = useMemo(() => {
        if (!supportsFilters || filter === FILTERS.ALL) return items;
        const coll = new Intl.Collator(lang === "ar" ? "ar" : "en", { sensitivity: "base" });
        const sorted = items.slice();
        switch (filter) {
            case FILTERS.A_Z:
                sorted.sort((a, b) => coll.compare(a.client_name || "", b.client_name || ""));
                return sorted;
            case FILTERS.Z_A:
                sorted.sort((a, b) => coll.compare(b.client_name || "", a.client_name || ""));
                return sorted;
            case FILTERS.GOLD_HAS:
                return items.filter((x) => (x.net_gold || 0) > 0);
            case FILTERS.GOLD_OWES:
                return items.filter((x) => (x.net_gold || 0) < 0);
            case FILTERS.CASH_HAS:
                return items.filter((x) => (x.net_fees || 0) > 0);
            case FILTERS.CASH_OWES:
                return items.filter((x) => (x.net_fees || 0) < 0);
            default:
                return items;
        }
    }, [items, filter, supportsFilters, lang]);

    return (
        <AppShell>
            <div className="mb-4 sm:mb-6">
                <Link to="/history" className="inline-flex items-center gap-1 text-sm font-medium text-neutral-600 hover:text-neutral-900" data-testid="back-to-records-picker">
                    ← {t.backToRecordsPicker}
                </Link>
                <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-amber-700 sm:text-sm">{t.historyKicker}</p>
                <h1 className="font-heading text-2xl font-bold sm:text-3xl lg:text-4xl">{pageTitle}</h1>
                <p className="mt-1 text-xs text-neutral-500 sm:text-sm">{pageSubtitle}</p>
            </div>

            <div className="mb-6 flex flex-wrap items-center gap-3">
                <div className="relative w-full max-w-md">
                    <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t.historySearchPlaceholder}
                        className="h-11 pe-10 ps-3"
                        data-testid="history-search-input"
                    />
                </div>
                {supportsFilters ? (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                type="button"
                                variant="outline"
                                className={`h-11 gap-2 border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 ${
                                    filter !== FILTERS.ALL ? "ring-2 ring-amber-300" : ""
                                }`}
                                data-testid="history-filter-btn"
                            >
                                <ArrowUpDown className="h-4 w-4" />
                                {t.sortFilterBtn}
                                {filter !== FILTERS.ALL ? (
                                    <span className="h-1.5 w-1.5 rounded-full bg-amber-600" aria-hidden />
                                ) : null}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-60" data-testid="history-filter-menu">
                            <DropdownMenuItem
                                onClick={() => setFilter(FILTERS.ALL)}
                                data-testid="history-filter-all"
                                className="cursor-pointer"
                            >
                                {filter === FILTERS.ALL ? <Check className="h-4 w-4" /> : <span className="w-4" />}
                                <span className="ms-2">{t.sortFilterAll}</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-neutral-500">
                                {t.sortFilterAlphabetGroup}
                            </DropdownMenuLabel>
                            <DropdownMenuItem
                                onClick={() => setFilter(FILTERS.A_Z)}
                                data-testid="history-filter-az"
                                className="cursor-pointer"
                            >
                                {filter === FILTERS.A_Z ? <Check className="h-4 w-4" /> : <span className="w-4" />}
                                <span className="ms-2">{t.sortFilterAToZ}</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() => setFilter(FILTERS.Z_A)}
                                data-testid="history-filter-za"
                                className="cursor-pointer"
                            >
                                {filter === FILTERS.Z_A ? <Check className="h-4 w-4" /> : <span className="w-4" />}
                                <span className="ms-2">{t.sortFilterZToA}</span>
                            </DropdownMenuItem>

                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-neutral-500">
                                {t.sortFilterGoldGroup}
                            </DropdownMenuLabel>
                            <DropdownMenuItem
                                onClick={() => setFilter(FILTERS.GOLD_HAS)}
                                data-testid="history-filter-gold-has"
                                className="cursor-pointer"
                            >
                                {filter === FILTERS.GOLD_HAS ? <Check className="h-4 w-4" /> : <span className="w-4" />}
                                <span className="ms-2 inline-flex items-center gap-1.5">
                                    <span className="h-2 w-2 rounded-full bg-red-600" aria-hidden />
                                    {t.sortFilterGoldOwed}
                                </span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() => setFilter(FILTERS.GOLD_OWES)}
                                data-testid="history-filter-gold-owes"
                                className="cursor-pointer"
                            >
                                {filter === FILTERS.GOLD_OWES ? <Check className="h-4 w-4" /> : <span className="w-4" />}
                                <span className="ms-2 inline-flex items-center gap-1.5">
                                    <span className="h-2 w-2 rounded-full bg-emerald-600" aria-hidden />
                                    {t.sortFilterGoldOwing}
                                </span>
                            </DropdownMenuItem>

                            {showCash ? (
                                <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-neutral-500">
                                        {t.sortFilterCashGroup}
                                    </DropdownMenuLabel>
                                    <DropdownMenuItem
                                        onClick={() => setFilter(FILTERS.CASH_HAS)}
                                        data-testid="history-filter-cash-has"
                                        className="cursor-pointer"
                                    >
                                        {filter === FILTERS.CASH_HAS ? <Check className="h-4 w-4" /> : <span className="w-4" />}
                                        <span className="ms-2 inline-flex items-center gap-1.5">
                                            <span className="h-2 w-2 rounded-full bg-red-600" aria-hidden />
                                            {t.sortFilterCashOwed}
                                        </span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        onClick={() => setFilter(FILTERS.CASH_OWES)}
                                        data-testid="history-filter-cash-owes"
                                        className="cursor-pointer"
                                    >
                                        {filter === FILTERS.CASH_OWES ? <Check className="h-4 w-4" /> : <span className="w-4" />}
                                        <span className="ms-2 inline-flex items-center gap-1.5">
                                            <span className="h-2 w-2 rounded-full bg-emerald-600" aria-hidden />
                                            {t.sortFilterCashOwing}
                                        </span>
                                    </DropdownMenuItem>
                                </>
                            ) : null}
                        </DropdownMenuContent>
                    </DropdownMenu>
                ) : null}
            </div>

            {supportsFilters && items.length > 0 ? (
                <GoldSummaryPanel items={items} lang={lang} />
            ) : null}

            {loading ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="h-36 animate-pulse rounded-2xl bg-neutral-200/60" />
                    ))}
                </div>
            ) : visibleItems.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-neutral-300 bg-white/60 p-12 text-center" data-testid="history-empty">
                    <FileText className="mx-auto mb-3 h-8 w-8 text-neutral-400" />
                    <h3 className="font-heading text-lg font-bold">
                        {filter !== FILTERS.ALL ? t.sortFilterEmpty : t.historyEmptyTitle}
                    </h3>
                    <p className="mt-1 text-sm text-neutral-500">{t.historyEmptyHint}</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="history-list">
                    {visibleItems.map((it) => {
                        const isDaily = kind === "daily";
                        const isVoucherCard = isVoucher;
                        const vd = it.voucher_data || {};
                        const positiveCash = (it.net_fees || 0) > 0;
                        const negativeCash = (it.net_fees || 0) < 0;
                        const netGold = it.net_gold || 0;
                        const positiveGold = netGold > 0;
                        const negativeGold = netGold < 0;
                        const dd = it.daily_data || {};
                        const dDate = it.daily_date || "";
                        const dSalesTotal = (dd.sales && dd.sales.total) || {};
                        const dPurBal = (dd.purchases && dd.purchases.balance) || {};
                        return (
                            <div
                                key={it.id}
                                className="group relative overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                                data-testid={`history-card-${it.id}`}
                            >
                                <Link to={`/history/${kind}/${it.id}`} className="block p-5">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <h3 className="font-heading text-lg font-bold leading-tight">
                                                {isDaily
                                                    ? (dDate || it.client_name)
                                                    : isVoucherCard
                                                        ? `${t.voucherTypeFieldLabel || "نوع السند"} #${String(it.voucher_number || vd.voucher_number || "----").padStart(4, "0")}`
                                                        : it.client_name}
                                            </h3>
                                            <p className="mt-0.5 text-xs text-neutral-500">
                                                {isVoucherCard
                                                    ? `${vd.voucher_date || it.voucher_date || ""}${vd.paid_to ? " · " + vd.paid_to : ""}`
                                                    : new Date(it.created_at).toLocaleString("en-GB")}
                                            </p>
                                        </div>
                                        {isDaily ? (
                                            <span
                                                className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ring-1 ${
                                                    it.save_status === "editable"
                                                        ? "bg-amber-50 text-amber-700 ring-amber-200"
                                                        : "bg-sky-50 text-sky-700 ring-sky-200"
                                                }`}
                                                data-testid={`history-status-chip-${it.id}`}
                                            >
                                                {it.save_status === "editable" ? t.statusEditableChip : t.statusFinalChip}
                                            </span>
                                        ) : (
                                            <ChevronLeft className="h-5 w-5 text-neutral-400 transition group-hover:-translate-x-1" />
                                        )}
                                    </div>
                                    {isVoucherCard ? (
                                        <div className="mt-4 grid grid-cols-2 gap-2">
                                            <div className="rounded-lg bg-amber-50 px-3 py-2 ring-1 ring-amber-200">
                                                <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">
                                                    {lang === "ar" ? "المبلغ (ر.ع)" : "Amount (R.O)"}
                                                </div>
                                                <div className="mt-0.5 font-numbers text-base font-bold text-amber-900">
                                                    {Number(vd.amount || 0).toLocaleString("en-US", { maximumFractionDigits: 3 })}
                                                </div>
                                            </div>
                                            <div className="rounded-lg bg-neutral-50 px-3 py-2 ring-1 ring-neutral-200">
                                                <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-600">
                                                    {lang === "ar" ? "العيار" : "Purity"}
                                                </div>
                                                <div className="mt-0.5 font-numbers text-base font-bold text-neutral-800">
                                                    {vd.gold_purity || "—"}
                                                </div>
                                            </div>
                                            {vd.being ? (
                                                <div className="col-span-2 truncate rounded-lg bg-neutral-50 px-3 py-1.5 text-xs text-neutral-700 ring-1 ring-neutral-100">
                                                    {vd.being}
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : isDaily ? (
                                        <div className="mt-4 grid grid-cols-3 gap-2">
                                            <SummaryChip label={t.dailyColCash} value={dSalesTotal.cash} />
                                            <SummaryChip label={t.dailyColTransfer} value={dSalesTotal.transfer} />
                                            <SummaryChip label={t.dailyColBank} value={dSalesTotal.bank} />
                                            <div className="col-span-3 rounded-lg bg-emerald-50/60 px-3 py-2 ring-1 ring-emerald-200">
                                                <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                                                    {t.dailyPurchasesTitle} · {t.dailyBalanceLabel}
                                                </div>
                                                <div className="mt-0.5 font-numbers text-base font-bold text-emerald-800">
                                                    {Number(dPurBal.weight || 0).toLocaleString("en-US", { maximumFractionDigits: 3 })}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className={`mt-4 grid gap-3 ${showCash ? "grid-cols-2" : "grid-cols-1"}`}>
                                            <div className="rounded-lg bg-neutral-50 px-3 py-2 ring-1 ring-neutral-100">
                                                <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">
                                                    {t.goldChip}
                                                </div>
                                                <div
                                                    className={`mt-0.5 font-numbers text-base font-bold ${
                                                        positiveGold ? "text-red-700" : negativeGold ? "text-emerald-700" : "text-neutral-500"
                                                    }`}
                                                >
                                                    {netGold.toLocaleString("en-US", { maximumFractionDigits: 3 })}
                                                </div>
                                            </div>
                                            {showCash ? (
                                                <div className="rounded-lg bg-neutral-50 px-3 py-2 ring-1 ring-neutral-100">
                                                    <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-700">
                                                        {t.cashChip}
                                                    </div>
                                                    <div
                                                        className={`mt-0.5 font-numbers text-base font-bold ${
                                                            positiveCash ? "text-red-700" : negativeCash ? "text-emerald-700" : "text-neutral-500"
                                                        }`}
                                                    >
                                                        {(it.net_fees || 0).toLocaleString("en-US", { maximumFractionDigits: 3 })}
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                    )}
                                </Link>

                                <div className="flex justify-end border-t border-neutral-100 px-3 py-2">
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setDeleteTarget({ id: it.id, client_name: it.client_name });
                                        }}
                                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-neutral-500 hover:bg-red-50 hover:text-red-700"
                                        data-testid={`history-delete-btn-${it.id}`}
                                        aria-label={t.deleteWord}
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                        {t.deleteWord}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <DeleteLedgerDialog
                open={!!deleteTarget}
                onOpenChange={(v) => {
                    if (!v) setDeleteTarget(null);
                }}
                ledgerId={deleteTarget?.id}
                clientName={deleteTarget?.client_name}
                onDeleted={() => {
                    if (deleteTarget) {
                        setItems((prev) => prev.filter((x) => x.id !== deleteTarget.id));
                    }
                    setDeleteTarget(null);
                }}
                testIdPrefix="history"
            />
        </AppShell>
    );
}

function SummaryChip({ label, value }) {
    return (
        <div className="rounded-lg bg-neutral-50 px-2.5 py-1.5 ring-1 ring-neutral-100">
            <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-600">{label}</div>
            <div className="mt-0.5 font-numbers text-sm font-bold text-neutral-800">
                {Number(value || 0).toLocaleString("en-US", { maximumFractionDigits: 3 })}
            </div>
        </div>
    );
}

// ─── Hierarchical daily records (Year → Month → Day) ─────────────────────
const MONTH_NAMES_AR = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
const MONTH_NAMES_EN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** Year → Month → Day tree. Reused by the manager for an employee's records
 *  (`empId` set): entries link to the read-only manager view and get a delete button. */
export function DailyHistoryTree({ t, lang, empId = null, title = null }) {
    const [tree, setTree] = useState({ years: [] });
    const [deleteTarget, setDeleteTarget] = useState(null);
    const treeUrl = empId ? `/auth/company/employees/${empId}/ledgers/tree` : "/ledgers/daily/tree";
    const entryPath = (id) => (empId ? `/employees/${empId}/daily/${id}` : `/history/daily/${id}`);
    const [loading, setLoading] = useState(true);
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const selYear = params.get("year") ? parseInt(params.get("year"), 10) : null;
    const selMonth = params.get("month") ? parseInt(params.get("month"), 10) : null;
    const selDay = params.get("day") ? parseInt(params.get("day"), 10) : null;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(treeUrl);
            setTree(r.data || { years: [] });
        } catch { setTree({ years: [] }); }
        finally { setLoading(false); }
    }, [treeUrl]);
    useEffect(() => { load(); }, [load]);
    useAutoRefresh(load, { intervalMs: 15000 });

    const yearObj = selYear ? tree.years?.find((y) => y.year === selYear) : null;
    const monthObj = yearObj && selMonth ? yearObj.months?.find((m) => m.month === selMonth) : null;
    const dayObj = monthObj && selDay ? monthObj.days?.find((d) => d.day === selDay) : null;

    // If a single day has exactly one record, jump straight into it.
    useEffect(() => {
        if (!empId && dayObj && dayObj.entries.length === 1) {
            navigate(`/history/daily/${dayObj.entries[0].id}`, { replace: true });
        }
    }, [dayObj, navigate, empId]);

    const setLevel = (level) => {
        const next = new URLSearchParams();
        if (level === "year") { /* clear all */ }
        else if (level === "month") { next.set("year", String(selYear)); }
        else if (level === "day")   { next.set("year", String(selYear)); next.set("month", String(selMonth)); }
        setParams(next);
    };

    const monthLabel = (m) => (lang === "ar" ? MONTH_NAMES_AR[m - 1] : MONTH_NAMES_EN[m - 1]);

    return (
        <AppShell>
            <div className="mb-4 sm:mb-6">
                <Link to={empId ? "/employees" : "/history"} className="inline-flex items-center gap-1 text-sm font-medium text-neutral-600 hover:text-neutral-900" data-testid="back-to-records-picker">
                    ← {empId ? (lang === "ar" ? "الموظفون" : "Employees") : t.backToRecordsPicker}
                </Link>
                <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-amber-700 sm:text-sm">{t.historyKicker}</p>
                <h1 className="font-heading text-2xl font-bold sm:text-3xl lg:text-4xl">{title || t.dailyRecordsTitle}</h1>
                <p className="mt-1 text-xs text-neutral-500 sm:text-sm">
                    {lang === "ar" ? "تنقّل حسب السنة ثم الشهر ثم اليوم — لا تظهر الأيام إلا إذا كان لها سجل فعلي" : "Drill down: year → month → day. Days appear only if they have real records"}
                </p>
            </div>

            {/* Breadcrumbs */}
            <div className="mb-4 flex flex-wrap items-center gap-1 text-sm" data-testid="dht-breadcrumbs">
                <button onClick={() => setLevel("year")} className={`rounded-full px-3 py-1 font-bold transition ${selYear == null ? "bg-amber-100 text-amber-800" : "text-neutral-500 hover:bg-neutral-100"}`} data-testid="dht-crumb-years">
                    {lang === "ar" ? "السنوات" : "Years"}
                </button>
                {selYear ? (
                    <>
                        <ChevronLeft className="h-3 w-3 text-neutral-400 rtl:rotate-180" />
                        <button onClick={() => setLevel("month")} className={`rounded-full px-3 py-1 font-bold transition ${selMonth == null ? "bg-amber-100 text-amber-800" : "text-neutral-500 hover:bg-neutral-100"}`} data-testid={`dht-crumb-year-${selYear}`}>{selYear}</button>
                    </>
                ) : null}
                {selMonth ? (
                    <>
                        <ChevronLeft className="h-3 w-3 text-neutral-400 rtl:rotate-180" />
                        <button onClick={() => setLevel("day")} className={`rounded-full px-3 py-1 font-bold transition ${selDay == null ? "bg-amber-100 text-amber-800" : "text-neutral-500 hover:bg-neutral-100"}`} data-testid={`dht-crumb-month-${selMonth}`}>{monthLabel(selMonth)}</button>
                    </>
                ) : null}
                {selDay ? (
                    <>
                        <ChevronLeft className="h-3 w-3 text-neutral-400 rtl:rotate-180" />
                        <span className="rounded-full bg-amber-100 px-3 py-1 font-bold text-amber-800" data-testid={`dht-crumb-day-${selDay}`}>{selDay}</span>
                    </>
                ) : null}
            </div>

            {loading ? (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                    {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-neutral-200/60" />)}
                </div>
            ) : tree.years?.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-neutral-300 bg-white/60 p-12 text-center" data-testid="dht-empty">
                    <FileText className="mx-auto mb-3 h-8 w-8 text-neutral-400" />
                    <h3 className="font-heading text-lg font-bold">{t.historyEmptyTitle}</h3>
                    <p className="mt-1 text-sm text-neutral-500">{lang === "ar" ? "ابدأ بإنشاء سجل حركة يومي وسيظهر تلقائياً هنا" : "Create a daily record and it will appear here automatically"}</p>
                </div>
            ) : !selYear ? (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="dht-years">
                    {tree.years.map((y) => (
                        <button key={y.year} onClick={() => setParams({ year: String(y.year) })} data-testid={`dht-year-${y.year}`}
                            className="group rounded-2xl border border-neutral-200 bg-white p-6 text-center shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
                            <Folder className="mx-auto h-8 w-8 text-amber-700" />
                            <div className="mt-2 font-numbers text-3xl font-bold text-neutral-800">{y.year}</div>
                            <div className="mt-1 text-[11px] text-neutral-500">{lang === "ar" ? `${y.count} سجل` : `${y.count} records`}</div>
                        </button>
                    ))}
                </div>
            ) : !selMonth ? (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="dht-months">
                    {(yearObj?.months || []).map((m) => (
                        <button key={m.month} onClick={() => setParams({ year: String(selYear), month: String(m.month) })} data-testid={`dht-month-${m.month}`}
                            className="group rounded-2xl border border-neutral-200 bg-white p-5 text-center shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
                            <FolderOpen className="mx-auto h-7 w-7 text-amber-700" />
                            <div className="mt-2 font-heading text-lg font-bold text-neutral-800">{monthLabel(m.month)}</div>
                            <div className="mt-1 text-[11px] text-neutral-500">{lang === "ar" ? `${m.count} سجل` : `${m.count} records`}</div>
                        </button>
                    ))}
                </div>
            ) : !selDay ? (
                <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7" data-testid="dht-days">
                    {(monthObj?.days || []).map((d) => (
                        <button key={d.day} onClick={() => setParams({ year: String(selYear), month: String(selMonth), day: String(d.day) })} data-testid={`dht-day-${d.day}`}
                            className="group flex flex-col items-center justify-center rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
                            <CalendarClock className="h-5 w-5 text-amber-700" />
                            <div className="mt-1 font-numbers text-2xl font-bold text-neutral-800">{d.day}</div>
                            <div className="text-[10px] text-neutral-500">{d.count > 1 ? (lang === "ar" ? `${d.count} سجلات` : `${d.count} records`) : (lang === "ar" ? "سجل" : "record")}</div>
                        </button>
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="dht-entries">
                    {(dayObj?.entries || []).map((e) => (
                        <div key={e.id} className="relative">
                            <Link to={entryPath(e.id)} className="block rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm transition hover:shadow-md" data-testid={`dht-entry-${e.id}`}>
                                <div className="flex items-center justify-between gap-2">
                                    <h3 className="font-heading font-bold">{e.date}{e.time ? <span className="ms-2 font-numbers text-xs font-medium text-neutral-500">{e.time}</span> : null}</h3>
                                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ring-1 ${empId ? "me-9" : ""} ${
                                        e.save_status === "editable" ? "bg-amber-50 text-amber-700 ring-amber-200" : "bg-sky-50 text-sky-700 ring-sky-200"
                                    }`}>
                                        {e.save_status === "editable" ? t.statusEditableChip : t.statusFinalChip}
                                    </span>
                                </div>
                            </Link>
                            {empId ? (
                                <button onClick={() => setDeleteTarget(e)} className="absolute end-3 top-3 rounded-lg p-1.5 text-rose-600 hover:bg-rose-50" data-testid={`dht-delete-${e.id}`} aria-label="delete">
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            ) : null}
                        </div>
                    ))}
                </div>
            )}
            {empId ? (
                <DeleteLedgerDialog
                    open={!!deleteTarget}
                    onOpenChange={(v) => !v && setDeleteTarget(null)}
                    ledgerId={deleteTarget?.id}
                    clientName={deleteTarget?.date}
                    deleteUrl={deleteTarget ? `/auth/company/employees/${empId}/ledgers/${deleteTarget.id}` : undefined}
                    onDeleted={() => { setDeleteTarget(null); load(); }}
                    testIdPrefix="emp-daily"
                />
            ) : null}
        </AppShell>
    );
}

